/**
 * Global Configuration for HSM Client Application
 * This file centralizes all URLs, API endpoints, and configuration values
 * 
 * 🚀 DEPLOYMENT INSTRUCTIONS:
 * ===========================
 * 1. Update the PROD_CONFIG URLs below with your actual production domain
 * 2. Change CURRENT_ENVIRONMENT from 'development' to 'production'
 * 3. That's it! All URLs will automatically switch to production
 * 
 * No environment variables needed - just update this single file!
 */

// ========================================
// 🚀 DEPLOYMENT CONFIGURATION
// ========================================
// Change these URLs when deploying to production
// Development URLs (current)
const DEV_CONFIG = {
    CLIENT_BASE_URL: 'http://localhost:3000',
    ADMIN_PANEL_BASE_URL: 'http://localhost/hsm/adminPanel',
    API_BASE_URL: 'http://localhost/hsm/adminPanel/api',
    ADMIN_LOGIN_URL: 'http://localhost/hsm/adminPanel',
    ADMIN_DASHBOARD_URL: 'http://localhost/hsm/adminPanel/dashboard',
    UPLOADS_BASE_URL: 'http://localhost/hsm/adminPanel/uploads',
    HSM_SERVER_URL: 'http://localhost:5000',
    HSM_SOCKET_URL: 'http://localhost:5000',
};

// Production URLs (update these for deployment)
const PROD_CONFIG = {
    CLIENT_BASE_URL: 'https://www.harshitaschoolofmusic.com',
    ADMIN_PANEL_BASE_URL: 'https://www.harshitaschoolofmusic.com/login',
    API_BASE_URL: 'https://www.harshitaschoolofmusic.com/login/api',
    ADMIN_LOGIN_URL: 'https://www.harshitaschoolofmusic.com/login',
    ADMIN_DASHBOARD_URL: 'https://www.harshitaschoolofmusic.com/login/dashboard',
    UPLOADS_BASE_URL: 'https://www.harshitaschoolofmusic.com/login/uploads',
    HSM_SERVER_URL: 'https://www.harshitaschoolofmusic.com',
    HSM_SOCKET_URL: 'https://www.harshitaschoolofmusic.com',
};

// ========================================
// 🔧 CURRENT ENVIRONMENT SETTING
// ========================================
// Set to 'development' for local development
// Set to 'production' for live deployment
const CURRENT_ENVIRONMENT = 'production'; // Change this to 'production' when deploying

// Select configuration based on current environment
const CONFIG = CURRENT_ENVIRONMENT === 'production' ? PROD_CONFIG : DEV_CONFIG;

// Base URLs Configuration
export const BASE_URLS = {
    // Main application URLs
    CLIENT_BASE_URL: CONFIG.CLIENT_BASE_URL,
    ADMIN_PANEL_BASE_URL: CONFIG.ADMIN_PANEL_BASE_URL,

    // API URLs
    API_BASE_URL: CONFIG.API_BASE_URL,

    // Admin Panel specific URLs
    ADMIN_LOGIN_URL: CONFIG.ADMIN_LOGIN_URL,
    ADMIN_DASHBOARD_URL: CONFIG.ADMIN_DASHBOARD_URL,

    // File upload URLs
    UPLOADS_BASE_URL: CONFIG.UPLOADS_BASE_URL,

    // HSM Node server (meeting API + Socket.io real-time)
    HSM_SERVER_URL: CONFIG.HSM_SERVER_URL,
    HSM_SOCKET_URL: CONFIG.HSM_SOCKET_URL,

    // Social Media URLs
    SOCIAL_MEDIA: {
        YOUTUBE: 'https://youtube.com/@harshitaschoolofmusic',
        INSTAGRAM: 'https://instagram.com/harshitaschoolofmusic',
        FACEBOOK: 'https://facebook.com/harshitaschoolofmusic',
    },

    // Contact Information
    CONTACT: {
        PHONE: '+91-8769373591',
        WHATSAPP: '+91-8769373591',
        EMAIL: 'harshitaschoolofmusic@gmail.com'
    },

    // External URLs
    EXTERNAL: {
        GOOGLE_MAPS_EMBED: 'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14228.40959997624!2d75.813232!3d26.931968!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396db1cb9953f405%3A0xcf8bcae9e2acbfe2!2sCad%20%26%20Render!5e0!3m2!1sen!2sin!4v1686727140755!5m2!1sen!2sin',
    }
};

// API Endpoints Configuration
export const API_ENDPOINTS = {
    // Class Management Endpoints
    CLASS: {
        GET_TEACHER_CLASSES_TODAY: '/get_teacher_classes_today.php',
        START_CLASS: '/start_class.php',
        GET_STUDENT_ACTIVE_CLASS: '/get_student_active_class.php',
        UPDATE_RECORDING_STATUS: '/update_recording_status.php',
        VERIFY_CLASS_PASSWORD: '/verify_class_password.php',
        COMPLETE_CLASS: '/complete_class.php',
        UPLOAD_CLASS_RECORDING: '/upload_class_recording.php',
        PERSONAL_DATA_DETECTED: '/personal_data_detected.php',
        UPDATE_CLASS_ATTENDANCE: '/update_class_attendance.php',
        CHECK_CLASS_ACCESS: '/check_class_access.php',
        CHECK_MEETING_STATUS: '/check_meeting_status.php',
        WEBRTC_SIGNALING: '/webrtc_signaling.php',
        CLEANUP_WEBRTC_SIGNALING: '/cleanup_webrtc_signaling.php',
        UPDATE_CLASS_UPDATE: '/update_class_update.php',
        GENERATE_GOOGLE_MEET: '/generate_google_meet.php',
        CLASS_CHAT_MESSAGES: '/class_chat_messages.php',
        CLASS_WHITEBOARD_NOTES: '/white_board.php',
    },

    // Authentication Endpoints
    AUTH: {
        LOGIN: '/login.php',
        REGISTER: '/register.php',
        LOGOUT: '/logout.php',
        REFRESH_TOKEN: '/refresh-token.php',
        FORGOT_PASSWORD: '/forgot-password.php',
        RESET_PASSWORD: '/reset-password.php',
        VERIFY_EMAIL: '/verify-email.php',
    },

    // User Management Endpoints
    USERS: {
        PROFILE: '/profile',
        UPDATE_PROFILE: '/update-profile',
        CHANGE_PASSWORD: '/change-password',
        UPLOAD_AVATAR: '/upload-avatar',
    },

    // Course Management Endpoints
    COURSES: {
        LIST: '/list',
        DETAILS: '/details',
        ENROLL: '/enroll',
        PROGRESS: '/progress',
        LESSONS: '/lessons',
        ASSIGNMENTS: '/assignments',
    },

    // Contact Endpoints
    CONTACT: {
        SEND_MESSAGE: '/contact_form.php',
        SUBSCRIBE_NEWSLETTER: '/subscribe-newsletter',
    },

    // Admission Endpoints
    ADMISSION: {
        SUBMIT_FORM: '/admission_form.php',
        GET_STATUS: '/status',
        UPLOAD_DOCUMENTS: '/upload-documents',
    },

    // Testimonials Endpoints
    TESTIMONIALS: {
        GET_ALL: '/testimonials.php',
    },

    // FAQ Endpoints
    FAQ: {
        GET_ALL: '/faqs.php',
    },
    
    // Instagram Feeds Endpoints
    INSTAFEEDS: {
        GET_ALL: '/instafeeds.php',
    },
};

// File Upload Paths
export const UPLOAD_PATHS = {
    CLASS_RECORDINGS: '/class_recordings',
    STUDY_MATERIALS: '/study_materials',
    STUDENT_TESTS: '/student_tests',
    ADMISSION_FORMS: '/admission_forms',
    POSTS: '/posts',
    POST_CATEGORIES: '/post_categories',
    POST_COMMENTS: '/post_comments',
    TESTIMONIALS: '/testimonials',
    USERS: '/users',
};

// Application Configuration
export const APP_CONFIG = {
    // API Configuration
    API_VERSION: 'v1',
    REQUEST_TIMEOUT: 30000, // 30 seconds

    // File Upload Configuration
    MAX_FILE_SIZE: 3 * 1024 * 1024 * 1024, // 3GB
    ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/webm', 'video/ogg'],
    ALLOWED_AUDIO_TYPES: ['audio/mp3', 'audio/wav', 'audio/ogg'],
    ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],

    // Pagination
    DEFAULT_PAGE_SIZE: 10,
    MAX_PAGE_SIZE: 100,

    // Cache Configuration
    CACHE_DURATION: 5 * 60 * 1000, // 5 minutes

    // Environment
    IS_DEVELOPMENT: CURRENT_ENVIRONMENT === 'development',
    IS_PRODUCTION: CURRENT_ENVIRONMENT === 'production',
};

// Helper Functions
export const getApiUrl = (endpoint) => {
    return `${BASE_URLS.API_BASE_URL}${endpoint}`;
};

export const getClassApiUrl = (endpoint) => {
    return getApiUrl(API_ENDPOINTS.CLASS[endpoint]);
};

/** Node meeting API (live meetings only - hsm-server, no adminPanel) */
export const getMeetingApiUrl = (path) => {
    const base = BASE_URLS.HSM_SERVER_URL || '';
    return base ? `${base}/api/meeting${path}` : '';
};

/** Socket.io URL for real-time meeting events (teacher_joined, etc.) */
export const getSocketUrl = () => BASE_URLS.HSM_SOCKET_URL || '';

export const getAuthApiUrl = (endpoint) => {
    return getApiUrl(API_ENDPOINTS.AUTH[endpoint]);
};

export const getAdmissionApiUrl = (endpoint) => {
    return getApiUrl(API_ENDPOINTS.ADMISSION[endpoint]);
};

export const getContactApiUrl = (endpoint) => {
    return getApiUrl(API_ENDPOINTS.CONTACT[endpoint]);
};

export const getTestimonialsApiUrl = (endpoint) => {
    return getApiUrl(API_ENDPOINTS.TESTIMONIALS[endpoint]);
};

export const getFaqApiUrl = (endpoint) => {
    return getApiUrl(API_ENDPOINTS.FAQ[endpoint]);
};

export const getInstafeedsApiUrl = (endpoint) => {
    return getApiUrl(API_ENDPOINTS.INSTAFEEDS[endpoint]);
};

export const getUploadUrl = (path, filename) => {
    return `${BASE_URLS.UPLOADS_BASE_URL}${path}/${filename}`;
};

export const getFullUrl = (path) => {
    return `${BASE_URLS.CLIENT_BASE_URL}${path}`;
};

// Metadata Configuration for SEO
export const METADATA_CONFIG = {
    BASE_URL: BASE_URLS.CLIENT_BASE_URL,
    SITE_NAME: 'Harshita School of Music',
    DEFAULT_TITLE: 'Harshita School of Music - Premier Music Education',
    DEFAULT_DESCRIPTION: 'Learn music from the best instructors at Harshita School of Music. Offering courses in piano, guitar, vocals, violin, and more.',
    DEFAULT_KEYWORDS: 'music school, music education, piano lessons, guitar lessons, vocal training, violin lessons, music production',
    DEFAULT_IMAGE: '/images/hsm_logo.png',
    TWITTER_HANDLE: '@harshitaschoolofmusic',
    FACEBOOK_APP_ID: 'your-facebook-app-id',
};

// Export all configurations as a single object for easy access
export const GLOBAL_CONFIG = {
    BASE_URLS,
    API_ENDPOINTS,
    UPLOAD_PATHS,
    APP_CONFIG,
    METADATA_CONFIG,
    // Helper functions
    getApiUrl,
    getClassApiUrl,
    getMeetingApiUrl,
    getSocketUrl,
    getAuthApiUrl,
    getAdmissionApiUrl,
    getContactApiUrl,
    getTestimonialsApiUrl,
    getFaqApiUrl,
    getInstafeedsApiUrl,
    getUploadUrl,
    getFullUrl,
};

export default GLOBAL_CONFIG;
